import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { BRAND_NAME, BRAND_SEAL } from './brand'
import {
	BREAKPOINT,
	ThemeContextValue,
	ThemeSwitch,
	tbClass,
	uiPrimitives,
	useIsMobile,
	useThemedStyles,
} from './theme'

/**
 * 站点顶栏 —— 编辑器 / 编曲练习库 / 分享页共用。
 *
 * 抽成独立模块的原因：设计稿里这三页的顶栏是**同一个** `.site-hd`
 * （印章 + 站名 + 竖线 + 曲名 ─── 工具组 + 主题开关）。站名取 `brand.ts`。
 * 各页各写一遍就会漂移成三个"长得差不多"的顶栏——历史上编曲练习库那版是酱紫色按钮，
 * 就是这么来的。
 *
 * 移动端顶栏**恒定一行**：每多一行顶栏就少一行谱面，所以只留「主动作 / 文件▼ / ⋯」三键，
 * 其余动作落进展开式抽屉。
 */
type HeaderActionBase = {
	key: string
	label: string
	variant?: 'plain' | 'outline' | 'primary'
	/** 归入「文件▼」下拉，而不是直接摆在工具条上 */
	file?: boolean
	/** 移动端顶栏直接显示（窄屏只有 3 个位置，要挑最常用的） */
	mobileRow?: boolean
}

/**
 * 一处顶栏动作。两种形态**二选一**，写成联合类型是为了让「同时给 onClick 与 href」
 * 根本写不出来 —— 那会连当前标签页一起导航走，而「不打断正在编辑的谱子」
 * 恰恰就是加 `href` 的**理由**，两个都给等于把这件事做反。
 *
 * 站内跳转继续走 `onClick`（`navigateHash`）；`href` 只给需要**真链接语义**的地方用
 * （中键新开、右键复制链接地址、悬停能看到地址）。
 */
/**
 * 「设置▼」这类**带子菜单的下拉动作**。子项只支持 onClick（跳转/开关都走回调，
 * 不需要 href —— 下拉里点一下不打断当前页，本就不该出真链接）。`separatorBefore`
 * 给该子项上方加一条分隔线，用于把「个人设置」这类低频项和其余项隔开。
 */
export type HeaderMenuItem = {
	label: string
	onClick?: () => void
	separatorBefore?: boolean
}

export type HeaderAction =
	| (HeaderActionBase & {onClick: () => void, href?: never, newTab?: never, disabled?: boolean})
	| (HeaderActionBase & {
		/** 只写裸 hash（如 `#tutorial`）：相对 URL 按当前文档解析，路径与 query 都保留，
		    天然跟着 `--base=./` 的子路径部署走 —— 别手拼 `location.origin + pathname`。 */
		href: string
		/** 新开标签页。不传则同标签页。 */
		newTab?: boolean
		onClick?: never
		disabled?: never
	})
	| (HeaderActionBase & {
		/** 下拉菜单动作：按钮显示 `label + ▼`，点开浮出 `menu` 里的子项。 */
		menu: HeaderMenuItem[]
		onClick?: never
		href?: never
		newTab?: never
		disabled?: never
	})

type PageHeaderProps = {
	/** 曲名 / 页面名，显示在站名右侧（窄屏替掉站名） */
	doc?: string
	/** 状态文字；移动端只显示为一个颜色圆点 */
	saveState?: string
	actions?: HeaderAction[]
	/**
	 * 桌面顶栏末尾的只读信息（登录邮箱等）。
	 * ⚠️ 没有内容时要传 `undefined`，**不要传一个空 `<span>`** —— 空节点也算"传了"，
	 * 会让「设置▾」以为有账号信息可收（见 `collapseSettings`），菜单里就多出一行空白。
	 */
	trailing?: ReactNode
	/**
	 * 点左侧品牌块回首页。不传则品牌块不可点（首页自己那张样张不传）。
	 * 传的是一个回调而不是 `href`：本站是 hash 路由，跳转统一走 `navigateHash`。
	 */
	onHome?: () => void
	/**
	 * 退出登录。桌面摆在 `trailing`（邮箱）**之后** —— 用户要求它在右上角最右侧；
	 * 窄屏顶栏没有它的位置，落进「⋯」抽屉末尾（与它作为普通动作时同一处）。
	 */
	onLogout?: () => void
	/**
	 * 把「主题开关 + trailing + 退出」收进「设置▾」下拉，给工具条腾宽度。
	 * 只给动作多的页面用（编辑器）；编曲练习库/分享/登录页工具条本来就空，摊开更好点。
	 *
	 * 登录态**没登录**时这三样都收不起来（没有邮箱、没有退出，只剩主题）——
	 * 那时顶栏本来也不挤，所以退回平铺，不为一个开关摆一个菜单（见 `collapseSettings`）。
	 */
	settingsMenu?: boolean
	children: ReactNode
	onKeyDown?: (evt: React.KeyboardEvent) => void
}

const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	/** 整页外壳：顶栏 + 正文两段，正文撑满剩余高度（谱面要吃掉所有余量） */
	outer: {
		minHeight: '100vh',
		background: t.palette.paper,
		color: t.palette.ink,
		fontFamily: t.fonts.sans,
		display: 'flex',
		flexDirection: 'column',
	},
	/**
	 * 通栏的底色条：只负责纸底与那条分隔线，横向内边距交给 `headerInner`。
	 * 内容贴边（`11px 16px`）会让品牌印记落在 x=16，而首页正文从 x=180 才开始，
	 * 两页并排一看就不像一个站。
	 */
	header: {
		padding: '11px 0',
		background: t.palette.paper2,
		borderBottom: `1px solid ${t.palette.line}`,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			padding: '7px 0',
		},
	},
	/**
	 * 顶栏内容列 —— 与首页 `HomePage.wrap` 同宽（1120）同边距（20 / 窄屏 12），
	 * 品牌印章因此与首页正文左缘对齐（1440 宽下两者都在 x=160）。
	 *
	 * ⚠️ 别加 `width:'100%'`。本站没有全局 `box-sizing:border-box`，`width:100%`
	 * 指的是**内容盒**，横向内边距会再加到外面 —— 窄屏下这一层就比视口宽 2×12px，
	 * 右侧的「保存 / ⋯」被挤出屏幕。`HomePage.wrap` 也没写 `width`，靠块级 auto 铺满。
	 */
	headerInner: {
		maxWidth: 1120,
		margin: '0 auto',
		paddingInline: 20,
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		flexWrap: 'wrap',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			// 移动端顶栏**恒定一行**：每多一行顶栏就少一行谱面
			flexWrap: 'nowrap',
			gap: 7,
			paddingInline: 12,
		},
	},
	brand: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		marginRight: 'auto',
		minWidth: 0,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			gap: 7,
		},
	},
	/**
	 * 品牌块变成按钮后的重置 —— 布局照用上面的 `brand`，这里只抹掉按钮的默认外观
	 * （不抹会多出一圈边框与灰底，印章那 30px 方块会走样）。
	 * 悬停压一点不透明度：整块可点，连印章一起给反馈最省事，也不用为三个不同的
	 * 子元素各写一条 hover。
	 */
	brandBtn: {
		border: 0,
		background: 'none',
		padding: 0,
		font: 'inherit',
		color: 'inherit',
		textAlign: 'left',
		cursor: 'pointer',
		transition: 'opacity .12s',
		'&:hover': {
			opacity: 0.82,
		},
	},
	/** 朱砂印章：全站唯一的品牌记号，用匾额体写一个「弹」 */
	brandSeal: {
		flex: 'none',
		width: 30,
		height: 30,
		display: 'grid',
		placeItems: 'center',
		background: t.palette.cinnabar,
		color: '#fff',
		borderRadius: t.radius.sm,
		fontFamily: t.fonts.brush,
		fontSize: 15,
		lineHeight: 1,
		userSelect: 'none',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			width: 24,
			height: 24,
			fontSize: 12,
		},
	},
	brandName: {
		fontFamily: t.fonts.brush,
		fontSize: 19,
		letterSpacing: '.09em',
		whiteSpace: 'nowrap',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			// 窄屏让位给曲名
			display: 'none',
		},
	},
	brandDiv: {
		flex: 'none',
		width: 1,
		height: 17,
		background: t.palette.line2,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			display: 'none',
		},
	},
	brandDoc: {
		fontFamily: t.fonts.serif,
		fontSize: 13.5,
		color: t.palette.ink2,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			flex: 1,
			minWidth: 0,
			fontSize: 13.5,
		},
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		flexWrap: 'wrap',
	},
	/** 移动端工具区：与 brand 同处一行，由顶栏 nowrap 约束 */
	toolbarMobile: {
		display: 'flex',
		alignItems: 'center',
		gap: 5,
		flex: 'none',
	},
	saveState: {
		fontSize: 12,
		color: t.palette.ink3,
		fontFamily: t.fonts.sans,
	},
	/** 窄屏下状态缩成一个小圆点：不占字宽，状态仍可见 */
	saveDot: {
		flex: 'none',
		width: 6,
		height: 6,
		borderRadius: '50%',
		background: t.palette.celadon,
	},
	saveDotBusy: { background: t.palette.gold },
	saveDotFail: { background: t.palette.cinnabar },

	// ── 「设置▾」下拉里的三块内容（主题 / 邮箱 / 退出）───────────────
	/**
	 * 比 `menu`（minWidth 148）宽一点：里面那排主题开关是三个中文字按钮，
	 * 而任何一条菜单项都不会有它们三个加起来宽。窄了会把「跟随|宣纸|夜墨」挤折行。
	 */
	menuWide: { minWidth: 176 },
	menuLabel: {
		fontSize: 11,
		color: t.palette.ink3,
		padding: '6px 10px 3px',
	},
	menuTheme: { padding: '0 6px 6px' },
	menuSep: {
		height: 1,
		background: t.palette.line,
		margin: '4px 6px',
	},
	/** 邮箱这类只读信息：与菜单项同样的内边距，但不是按钮（没有 hover、点不动） */
	menuInfo: {
		padding: '7px 10px',
		fontSize: 12,
		color: t.palette.ink3,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

	/** 移动端「更多⋯」：从顶栏下方整条展开的抽屉 */
	drawer: {
		background: t.palette.paper2,
		borderBottom: `1px solid ${t.palette.line}`,
		padding: 5,
		display: 'flex',
		flexDirection: 'column',
	},
	drawerItem: {
		textAlign: 'left',
		fontFamily: t.fonts.serif,
		fontSize: 13.5,
		color: t.palette.ink,
		padding: '12px',
		border: 0,
		borderRadius: t.radius.sm,
		background: 'none',
		cursor: 'pointer',
		// 「教程」在窄屏是落进这个抽屉的（既非 mobileRow 也非 file），出的是 <a>：
		// 不补这一行，手机上它会是抽屉里唯一带下划线的一项。
		textDecoration: 'none',
		'&:hover': {
			background: t.palette.celadon2,
			color: t.palette.celadonInk,
		},
	},
	drawerSep: {
		height: 1,
		background: t.palette.line,
		margin: '4px 8px',
	},
	drawerTheme: {
		padding: '6px 8px 8px',
	},
	inner: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
	},
})

const useStyles = () => useThemedStyles(stylesFactory)

/**
 * 移动端顶栏放不下文字状态，只留一个圆点，颜色即语义。
 * 从文案推导而不是另开一个 state —— 这些文案只有页面里那一处产生，
 * 多一个 state 就多一处可能与文案不同步的地方。
 */
function saveToneOf(text: string): 'ok' | 'busy' | 'fail' {
	if (!text) return 'ok'
	if (/失败|错误/.test(text)) return 'fail'
	if (/中…/.test(text)) return 'busy'
	return 'ok'
}

/**
 * 点外部 / 按 Esc 关闭菜单。
 * 传 setState 而不是回调：useState 的 setter 引用稳定，effect 才不会被无谓地重挂。
 */
function useMenuDismiss(open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>) {
	const ref = useRef<HTMLDivElement | null>(null)
	useEffect(() => {
		if (!open) return
		const onDown = (evt: MouseEvent) => {
			if (ref.current && !ref.current.contains(evt.target as Node)) setOpen(false)
		}
		const onKey = (evt: KeyboardEvent) => {
			if (evt.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [open, setOpen])
	return ref
}

/**
 * 一处动作 = 一个元素：给了 `href` 出 `<a>`，否则出 `<button>`。
 *
 * ⚠️ `className` 由**调用点原样传进来**。四个渲染位置的类串各不相同 ——
 *    桌面工具条走 `tbClass`，窄屏动作行不走（无 variant 会落到 `tbOutline` 而不是 `tb`），
 *    抽屉是 `drawerItem`，文件▾ 是 `menuItem`。在这里"顺手统一"成一份就是改配色。
 * ⚠️ 别加 `type="button"`：现有四处 `<button>` 都没有它，加上就是凭空多一个属性。
 * ⚠️ 别在这里关抽屉/关菜单 —— 那是各调用点自己的状态，包成 `onActivate` 传进来。
 */
function renderAction(a: HeaderAction, className: string, onActivate?: () => void) {
	if (a.href) return <a key={a.key} href={a.href} className={className}
		target={a.newTab ? '_blank' : undefined}
		rel={a.newTab ? 'noreferrer' : undefined}
		onClick={onActivate}>{a.label}</a>
	return <button key={a.key} className={className} disabled={a.disabled}
		onClick={onActivate}>{a.label}</button>
}

export function PageHeader(props: PageHeaderProps) {
	const classes = useStyles()
	const isMobile = useIsMobile()
	const [fileOpen, setFileOpen] = useState(false)
	const [moreOpen, setMoreOpen] = useState(false)
	const [settingsOpen, setSettingsOpen] = useState(false)
	// 当前展开的「下拉菜单动作」（key 对应 HeaderAction.key）。同屏最多一个菜单展开。
	const [menuKey, setMenuKey] = useState<string | null>(null)
	const fileRef = useMenuDismiss(fileOpen, setFileOpen)
	const settingsRef = useMenuDismiss(settingsOpen, setSettingsOpen)
	const menuRef = useMenuDismiss(menuKey != null, v => setMenuKey(v ? menuKey : null))

	const actions = props.actions ?? []
	const barActions = actions.filter(a => !a.file)
	const fileActions = actions.filter(a => a.file)
	// 下拉菜单动作单独列出：桌面端渲染成「label▼」，移动端拆成子项进抽屉。
	const menuActions = actions.filter(a => 'menu' in a)
	const plainBarActions = barActions.filter(a => !('menu' in a))
	const rowActions = actions.filter(a => a.mobileRow && !a.file && !('menu' in a))
	const drawerActions = plainBarActions.filter(a => !a.mobileRow)
	const tone = saveToneOf(props.saveState ?? '')
	// 只为「主题」摆一个菜单是净亏：开关本身就得先点两下才看得见。
	// 所以没登录（没有邮箱、没有退出）时即使传了 settingsMenu 也退回平铺。
	const collapseSettings = !!props.settingsMenu && (props.trailing != null || !!props.onLogout)

	// 折叠菜单在窄屏/宽屏切换时不留残影：布局一变就全部收起
	useEffect(() => {
		setFileOpen(false)
		setMoreOpen(false)
		setSettingsOpen(false)
		setMenuKey(null)
	}, [isMobile])

	// 抽屉是整条展开的面板而不是浮层，不做点外部关闭；但 Esc 得管
	useEffect(() => {
		if (!moreOpen) return
		const onKey = (evt: KeyboardEvent) => {
			if (evt.key === 'Escape') setMoreOpen(false)
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [moreOpen])

	const brandContent = <>
		<span className={classes.brandSeal}>{BRAND_SEAL}</span>
		<span className={classes.brandName}>{BRAND_NAME}</span>
		<span className={classes.brandDiv} />
		{props.doc && <span className={classes.brandDoc} title={props.doc}>{props.doc}</span>}
	</>
	// 挂 onHome 才做成按钮：没这个回调的页面（首页的样张）里，一块点不动的
	// `cursor:pointer` 比不可点更糟。曲名也在块里 —— 整块进整块出，不做半个可点。
	const brand = props.onHome
		? <button type='button' className={`${classes.brand} ${classes.brandBtn}`}
			onClick={props.onHome} title="回到首页" aria-label="回到首页">
			{brandContent}
		</button>
		: <div className={classes.brand}>{brandContent}</div>

	if (isMobile) {
		return <div className={classes.outer} onKeyDown={props.onKeyDown}>
			<div className={classes.header}>
				<div className={classes.headerInner}>
					{brand}
					{props.saveState && <span
						className={`${classes.saveDot} ${tone === 'busy' ? classes.saveDotBusy : tone === 'fail' ? classes.saveDotFail : ''}`}
						title={props.saveState} />}
					<div className={classes.toolbarMobile}>
						{rowActions.map(a => renderAction(a,
							`${classes.tb} ${classes.tbMobile} ${a.variant === 'primary' ? classes.tbPrimary : classes.tbOutline}`,
							a.onClick))}
						{fileActions.length > 0 && <div className={classes.menuWrap} ref={fileRef}>
							<button className={`${classes.tb} ${classes.tbMobile} ${classes.tbOutline}`}
								aria-haspopup="true" aria-expanded={fileOpen}
								onClick={() => setFileOpen(v => !v)}>文件▼</button>
							{fileOpen && <div className={classes.menu}>
								{fileActions.map(a => renderAction(a, classes.menuItem,
									() => {setFileOpen(false); a.onClick?.()}))}
							</div>}
						</div>}
						<button className={`${classes.tb} ${classes.tbMobile}`}
							aria-label="更多" aria-haspopup="true" aria-expanded={moreOpen}
							onClick={() => setMoreOpen(v => !v)}>⋯</button>
					</div>
				</div>
			</div>
			{/* 抽屉是整条展开的面板（不是浮层菜单），所以只收 Esc，不做点外部关闭 */}
			{moreOpen && <div className={classes.drawer}>
				{drawerActions.map(a => renderAction(a, classes.drawerItem,
					() => {setMoreOpen(false); a.onClick?.()}))}
				{/* 下拉菜单动作在窄屏拆成子项直接铺进抽屉（没有位置摆二级浮层） */}
				{menuActions.map(a => a.menu.map((it, i) => <React.Fragment key={a.key + i}>
					{it.separatorBefore && <div className={classes.drawerSep} />}
					<button className={classes.drawerItem}
						onClick={() => {setMoreOpen(false); it.onClick?.()}}>{it.label}</button>
				</React.Fragment>))}
				{props.onLogout && <button className={classes.drawerItem}
					onClick={() => {setMoreOpen(false); props.onLogout!()}}>退出</button>}
				<div className={classes.drawerSep} />
				<div className={classes.drawerTheme}><ThemeSwitch /></div>
			</div>}
			<div className={classes.inner}>{props.children}</div>
		</div>
	}

	return <div className={classes.outer} onKeyDown={props.onKeyDown}>
		<div className={classes.header}>
			<div className={classes.headerInner}>
				{brand}
				<div className={classes.toolbar}>
					{props.saveState && <span className={classes.saveState}>{props.saveState}</span>}
					{plainBarActions.map(a => renderAction(a, tbClass(classes, a.variant), a.onClick))}
					{/* 下拉菜单动作（设置▼ 等）：一个按钮浮出子项。同屏可能多个，各管各的 key。 */}
					{menuActions.map(a => {
						const open = menuKey === a.key
						return <div key={a.key} className={classes.menuWrap} ref={menuRef}>
							<button className={tbClass(classes, a.variant)}
								aria-haspopup="true" aria-expanded={open}
								onClick={() => setMenuKey(open ? null : a.key)}>{a.label}▼</button>
							{open && <div className={classes.menu}>
								{a.menu.map((it, i) => <React.Fragment key={i}>
									{it.separatorBefore && <div className={classes.menuSep} />}
									<button className={classes.menuItem}
										onClick={() => {setMenuKey(null); it.onClick?.()}}>{it.label}</button>
								</React.Fragment>)}
							</div>}
						</div>
					})}
					{fileActions.length > 0 && <div className={classes.menuWrap} ref={fileRef}>
						<button className={`${classes.tb} ${classes.tbOutline}`}
							aria-haspopup="true" aria-expanded={fileOpen}
							onClick={() => setFileOpen(v => !v)}>文件▼</button>
						{fileOpen && <div className={classes.menu}>
							{fileActions.map(a => renderAction(a, classes.menuItem,
								() => {setFileOpen(false); a.onClick?.()}))}
						</div>}
					</div>}
					{collapseSettings
						? <div className={classes.menuWrap} ref={settingsRef}>
							<button className={`${classes.tb} ${classes.tbOutline}`}
								aria-haspopup="true" aria-expanded={settingsOpen}
								onClick={() => setSettingsOpen(v => !v)}>设置▼</button>
							{settingsOpen && <div className={`${classes.menu} ${classes.menuWide}`}>
								{/* 主题不折成三条菜单项：ThemeSwitch 是**同一个组件**（移动端 ⋯ 抽屉里
								    也是它），折一份出来就有两处会各自漂移的"主题开关" */}
								<div className={classes.menuLabel}>主题</div>
								<div className={classes.menuTheme}><ThemeSwitch /></div>
								{(props.trailing != null || props.onLogout) && <div className={classes.menuSep} />}
								{props.trailing != null && <div className={classes.menuInfo}>{props.trailing}</div>}
								{props.onLogout && <button className={classes.menuItem}
									onClick={() => {setSettingsOpen(false); props.onLogout!()}}>退出</button>}
							</div>}
						</div>
						: <>
							<ThemeSwitch />
							{props.trailing}
							{props.onLogout && <button className={tbClass(classes)} onClick={props.onLogout}>退出</button>}
						</>}
				</div>
			</div>
		</div>
		<div className={classes.inner}>{props.children}</div>
	</div>
}
